import {
  PARTICLE_HARD_CAP,
  PERFECT_SPARKLE_COLOUR,
} from './pianoRenderConstants';
import { colourForEmission } from './pianoRenderColour';
import type {
  PianoLaneFlash,
  PianoLaneFlashSpec,
  PianoParticle,
  PianoParticleEmission,
  PianoShockwave,
  PianoShockwaveSpec,
} from './pianoRenderTypes';
import type { RenderQuality } from '../../rendering/renderQuality';

/**
 * Owns the three long-lived FX pools (particles, shockwaves, lane
 * flashes) for the Piano renderer. The orchestrator forwards
 * incoming spec arrays (consumed via WeakSets so duplicate frames
 * don't double-spawn) and the live entries are aged + drawn each
 * frame. Pulled into a single class because all three share the
 * same shape — consume specs → advance time → draw — and they're
 * always processed back-to-back inside the draw cycle.
 */
export class PianoFxSystem {
  private particles: PianoParticle[] = [];
  private shockwaves: PianoShockwave[] = [];
  private laneFlashes: PianoLaneFlash[] = [];
  private reducedMotion: boolean;
  private quality: RenderQuality;
  /**
   * Effective hard cap on alive particle count. Scaled by the quality
   * tier so low-tier devices don't pay the per-particle GPU cost of
   * the full 600-particle ceiling. Stored on the instance so the
   * scale is applied uniformly across emission/cleanup paths.
   */
  private particleHardCap: number;
  /** Tracks which emissions / specs we've already consumed (by reference) to avoid double-spawning across frames. */
  private consumedEmissions = new WeakSet<PianoParticleEmission>();
  private consumedShockwaves = new WeakSet<PianoShockwaveSpec>();
  private consumedLaneFlashes = new WeakSet<PianoLaneFlashSpec>();

  constructor(reducedMotion: boolean, quality: RenderQuality) {
    this.reducedMotion = reducedMotion;
    this.quality = quality;
    this.particleHardCap = Math.round(PARTICLE_HARD_CAP * quality.particleScale);
  }

  /**
   * Round `baseCount` down by the active particle-scale tier and
   * floor it at one particle so a burst never silently disappears.
   * Used by every spawn helper that picks how many particles to push
   * into the pool.
   */
  private scaleCount(baseCount: number): number {
    if (this.quality.particleScale >= 1) {
      return baseCount;
    }
    return Math.max(1, Math.round(baseCount * this.quality.particleScale));
  }

  /**
   * Drop all live FX. Used on loop boundaries so a residual particle
   * / shockwave from loop N doesn't ghost-render through loop N+1's
   * opening countdown.
   */
  clear(): void {
    this.particles.length = 0;
    this.shockwaves.length = 0;
    this.laneFlashes.length = 0;
  }

  /** Snapshot for the draw pass — caller iterates without mutating. */
  getLaneFlashes(): ReadonlyArray<PianoLaneFlash> {
    return this.laneFlashes;
  }

  /**
   * Spawn an unconsumed-shockwave directly. Used by overlay code that
   * wants to attach a ring to a banner without going through the
   * session's spec queue.
   */
  spawnShockwaveDirect(spec: Omit<PianoShockwave, 'maxLife'>): void {
    if (this.reducedMotion) {
      return;
    }
    this.shockwaves.push({ ...spec, maxLife: spec.life });
  }

  /**
   * Spawn particles from each newly seen emission spec. Counts and
   * speeds scale with verdict type so a perfect explodes harder than
   * a good, and a miss puffs downward (negative gravity bias) instead
   * of celebrating upward.
   */
  consumeEmissions(emissions: ReadonlyArray<PianoParticleEmission>): void {
    if (emissions.length === 0) {
      return;
    }
    for (const emission of emissions) {
      if (this.consumedEmissions.has(emission)) {
        continue;
      }
      this.consumedEmissions.add(emission);
      this.spawnEmission(emission);
    }
    if (this.particles.length > this.particleHardCap) {
      this.particles.splice(0, this.particles.length - this.particleHardCap);
    }
  }

  consumeShockwaveSpecs(specs: ReadonlyArray<PianoShockwaveSpec>): void {
    if (specs.length === 0 || this.reducedMotion) {
      return;
    }
    for (const spec of specs) {
      if (this.consumedShockwaves.has(spec)) {
        continue;
      }
      this.consumedShockwaves.add(spec);
      this.shockwaves.push({
        x: spec.x,
        y: spec.y,
        startR: spec.startR,
        endR: spec.endR,
        life: spec.life,
        maxLife: spec.life,
        thickness: spec.thickness,
        colour: spec.colour,
        alpha: spec.alpha,
      });
    }
  }

  consumeLaneFlashSpecs(specs: ReadonlyArray<PianoLaneFlashSpec>): void {
    if (specs.length === 0) {
      return;
    }
    for (const spec of specs) {
      if (this.consumedLaneFlashes.has(spec)) {
        continue;
      }
      this.consumedLaneFlashes.add(spec);
      this.laneFlashes.push({
        lane: spec.lane,
        life: spec.life,
        maxLife: spec.life,
        alpha: spec.alpha,
        colour: spec.colour,
      });
    }
  }

  advanceAll(dt: number): void {
    if (dt <= 0) {
      return;
    }
    this.advanceParticles(dt);
    this.advanceShockwaves(dt);
    this.advanceLaneFlashes(dt);
  }

  drawParticles(ctx: CanvasRenderingContext2D): void {
    if (this.particles.length === 0) {
      return;
    }
    ctx.save();
    for (const p of this.particles) {
      const t = p.life / p.maxLife;
      const a = t * t;
      ctx.globalAlpha = Math.max(0, Math.min(1, a));
      ctx.fillStyle = p.colour;
      const size = p.size * (0.5 + 0.5 * t);
      ctx.beginPath();
      ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  drawShockwaves(ctx: CanvasRenderingContext2D): void {
    if (this.shockwaves.length === 0) {
      return;
    }
    ctx.save();
    for (const w of this.shockwaves) {
      const t = w.life / w.maxLife;
      const eased = 1 - t;
      const r = w.startR + (w.endR - w.startR) * eased;
      const alpha = w.alpha * t;
      if (alpha <= 0.01) {
        continue;
      }
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = w.colour;
      ctx.lineWidth = w.thickness * (0.5 + t * 0.5);
      if (!this.reducedMotion && this.quality.chromeShadows) {
        ctx.shadowColor = w.colour;
        ctx.shadowBlur = 12 * alpha;
      }
      ctx.beginPath();
      ctx.arc(w.x, w.y, r, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  private spawnEmission(emission: PianoParticleEmission): void {
    if (emission.type === 'hold_sustain') {
      this.spawnHoldSustain(emission);
      return;
    }
    if (emission.type === 'hold_release') {
      this.spawnHoldReleasePlume(emission);
      return;
    }
    const baseCount =
      emission.type === 'death'
        ? 40
        : emission.type === 'milestone'
          ? 28
          : emission.type === 'hit_perfect'
            ? 18
            : emission.type === 'hit_good'
              ? 8
              : 6;
    const count = this.reducedMotion
      ? Math.max(2, Math.round(baseCount * 0.25))
      : this.scaleCount(baseCount);
    const colour = emission.colour ?? colourForEmission(emission.type);
    const speedBase =
      emission.type === 'death'
        ? 280
        : emission.type === 'milestone'
          ? 240
          : emission.type === 'hit_perfect'
            ? 200
            : emission.type === 'hit_good'
              ? 150
              : 70;
    // Perfect / good / milestone celebrate upward (positive bias);
    // miss settles downward (negative bias is added to vy) so the body
    // language reads "ignored" rather than "celebrated".
    const upBias =
      emission.type === 'hit_perfect'
        ? 70
        : emission.type === 'milestone'
          ? 100
          : emission.type === 'hit_good'
            ? 40
            : emission.type === 'hit_miss'
              ? -80
              : 0;
    for (let i = 0; i < count; i += 1) {
      const angle = Math.random() * Math.PI * 2;
      const speed = speedBase * (0.4 + Math.random() * 0.8);
      const life = 0.45 + Math.random() * 0.5;
      this.particles.push({
        x: emission.x,
        y: emission.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - upBias,
        life,
        maxLife: life,
        colour,
        size: 2 + Math.random() * (emission.type === 'milestone' ? 4.5 : 3.0),
      });
    }

    if (emission.type === 'hit_perfect' && !this.reducedMotion) {
      this.spawnPerfectAccents(emission.x, emission.y, colour);
    }

    if (emission.type === 'death' && !this.reducedMotion) {
      // Big radial ring on death so the impact reads as "the world
      // ruptured" rather than only "particles puffed". Sized larger
      // than tap-resolution shockwaves to telegraph terminality.
      this.shockwaves.push({
        x: emission.x,
        y: emission.y,
        startR: 30,
        endR: 360,
        life: 0.85,
        maxLife: 0.85,
        thickness: 4,
        colour,
        alpha: 0.85,
      });
    }
  }

  /**
   * Single ambient sparkle pushed off the hit line while a hold is
   * actively engaged. Fired by the session at a steady cadence (one
   * per ~110ms per held lane) so the player has continuous "you are
   * scoring right now" feedback distinct from per-resolution bursts.
   * Tiny, upward-biased, short-lived so a 30s hold doesn't snow the
   * playfield.
   */
  private spawnHoldSustain(emission: PianoParticleEmission): void {
    if (this.reducedMotion) {
      return;
    }
    const colour = emission.colour ?? colourForEmission('hit_good');
    const xJit = (Math.random() - 0.5) * 24;
    const vy = -160 - Math.random() * 80;
    const life = 0.35 + Math.random() * 0.25;
    this.particles.push({
      x: emission.x + xJit,
      y: emission.y,
      vx: (Math.random() - 0.5) * 30,
      vy,
      life,
      maxLife: life,
      colour,
      size: 1.2 + Math.random() * 1.4,
    });
  }

  /**
   * Tall vertical plume spawned when a hold note is released
   * successfully. Distinct from a tap-verdict radial burst: this
   * reads as "energy stored in the hold escaping upward," rewarding
   * the player for completing the longer commitment. Layered ON TOP
   * of the regular per-verdict burst — the radial says "you hit the
   * timing," the plume says "you held it all the way."
   */
  private spawnHoldReleasePlume(emission: PianoParticleEmission): void {
    if (this.reducedMotion) {
      return;
    }
    const colour = emission.colour ?? colourForEmission('hit_perfect');
    const columnCount = this.scaleCount(14);
    for (let i = 0; i < columnCount; i += 1) {
      const xJit = (Math.random() - 0.5) * 26;
      const vyBase = -290 - Math.random() * 140;
      const vxJit = (Math.random() - 0.5) * 50;
      const life = 0.5 + Math.random() * 0.3;
      this.particles.push({
        x: emission.x + xJit,
        y: emission.y,
        vx: vxJit,
        vy: vyBase,
        life,
        maxLife: life,
        colour,
        size: 1.8 + Math.random() * 1.8,
      });
    }
  }

  /**
   * Two-layer accent on perfect hits: a small, slow-drifting cyan
   * sparkle cloud (pale + long-lived, reads as "shimmering residue")
   * and a tight upward spark column (low x-spread, strong negative
   * vy, reads as "energy released" rising out of the hit line). Both
   * skipped under reduced-motion via the caller's gate; the main
   * burst alone already conveys the verdict.
   */
  private spawnPerfectAccents(x: number, y: number, mainColour: string): void {
    const sparkleCount = this.scaleCount(5);
    for (let i = 0; i < sparkleCount; i += 1) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 80 + Math.random() * 120;
      const life = 0.6 + Math.random() * 0.4;
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 90,
        life,
        maxLife: life,
        colour: PERFECT_SPARKLE_COLOUR,
        size: 1 + Math.random() * 1.6,
      });
    }
    const columnCount = this.scaleCount(4);
    for (let i = 0; i < columnCount; i += 1) {
      const xJit = (Math.random() - 0.5) * 24;
      const vyBase = -260 - Math.random() * 100;
      const vxJit = (Math.random() - 0.5) * 60;
      const life = 0.45 + Math.random() * 0.25;
      this.particles.push({
        x: x + xJit,
        y,
        vx: vxJit,
        vy: vyBase,
        life,
        maxLife: life,
        colour: mainColour,
        size: 2 + Math.random() * 1.5,
      });
    }
  }

  private advanceParticles(dt: number): void {
    if (this.particles.length === 0) {
      return;
    }
    const gravity = 280;
    const drag = 0.92;
    for (let i = this.particles.length - 1; i >= 0; i -= 1) {
      const p = this.particles[i];
      p.life -= dt;
      if (p.life <= 0) {
        this.particles.splice(i, 1);
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy += gravity * dt;
      p.vx *= Math.pow(drag, dt * 60);
    }
  }

  private advanceShockwaves(dt: number): void {
    if (this.shockwaves.length === 0) {
      return;
    }
    for (let i = this.shockwaves.length - 1; i >= 0; i -= 1) {
      const w = this.shockwaves[i];
      w.life -= dt;
      if (w.life <= 0) {
        this.shockwaves.splice(i, 1);
      }
    }
  }

  private advanceLaneFlashes(dt: number): void {
    if (this.laneFlashes.length === 0) {
      return;
    }
    for (let i = this.laneFlashes.length - 1; i >= 0; i -= 1) {
      const f = this.laneFlashes[i];
      f.life -= dt;
      if (f.life <= 0) {
        this.laneFlashes.splice(i, 1);
      }
    }
  }
}
